/**
 * The dates worth campaigning on, for the country a workspace is in.
 *
 * ## WHY THIS IS A CURATED LIST AND NOT THE DATASET
 *
 * The extracted dataset is 394 rows over two years, and it cannot drive a screen
 * as it stands. Measured on it:
 *
 *   - **368 of 394 are marked "only in certain areas"** — state holidays,
 *     regional observances, days off in one district.
 *   - **85 of 241 distinct dates carry more than one row.** 20 October 2026 has
 *     six, all meaning the thing a marketer calls Dussehra.
 *   - A 60-day window from 19 September 2026 returns **47 entries**, of which
 *     45 are regional. Among them: "September Equinox", a death anniversary and
 *     a state martyrdom day. Through {@link CALENDAR_MOMENTS} the same window
 *     returns **eight**.
 *
 * {@link CALENDAR_MOMENTS} is the subset a business plans around.
 *
 * ## AND WHY THE NAMES ARE HERE RATHER THAN IN THE DATA
 *
 * The source calls Diwali **"Festival of Lights"**, Holi **"Festival of
 * Colors"**, Eid al-Fitr **"Festival of Breaking the Fast"** and Dussehra
 * **"Vijayadashami"**. Those are the wrong words to put in front of an Indian
 * retailer — *"Festival of Lights is in 51 days"* reads as a product that has
 * never met its market.
 *
 * The extractor copies them verbatim on purpose, so the file stays traceable to
 * what was extracted. **This map is where the product speaks**, keyed on the
 * source's stable slug rather than on its prose.
 *
 * ## ONE ROW PER DATE, NOT PER FESTIVAL
 *
 * An entry is a **day**, and it carries every name that falls on it. This was
 * one row per festival until review measured what that produced: on **66% of
 * days the list held at least one pair of rows sharing a date**, and 14 April
 * 2026 drew four — Baisakhi, Bengali New Year, Tamil New Year and Ambedkar
 * Jayanti — as four identical `APR 14` chips with four identical countdowns.
 *
 * Worse, the tie-break was alphabetical by slug, so `festival-of-lights` sorted
 * above `naraka-chaturdasi` and the card printed **"Diwali" above "Choti
 * Diwali"** — the lesser day under the greater one, on the same date, which is
 * definitionally impossible. A marketer plans **one campaign per planning day**,
 * so a day is the unit. No name is dropped; they share a line.
 *
 * ## A FESTIVAL SPANNING CONSECUTIVE DAYS IS ONE MOMENT. A GAP IS NOT.
 *
 * Pongal is 14 *and* 15 January 2027; the Eids run over two days. Those collapse
 * to the first day, and {@link CALENDAR_RUN_GAP_DAYS} is what "consecutive"
 * means.
 *
 * **It used to collapse anything within three days, and that shipped a wrong
 * date.** The source lists `raksha-bandhan` on 26 *and* 28 August 2026 — two
 * days apart, not consecutive — so the fold discarded the 28th and printed the
 * 26th. Raksha Bandhan 2026 is Friday the 28th, which is also the row the source
 * itself flags as the long weekend. A retailer briefing a Rakhi campaign off
 * that card lands two days early, which for Rakhi is the whole campaign.
 *
 * So a gap is **not** collapsed: both dates are kept and a reader sees the
 * ambiguity the source actually contains. Choosing silently between them is what
 * put Rakhi on the wrong day, and there is nothing in the data to choose with.
 *
 * ## THE COUNTDOWN IS NOT IN HERE, AND THE WINDOW HAS SLACK
 *
 * `daysAway` used to ride on the wire, computed on the server from
 * `toISOString()` — the **UTC** date. The reader's "today" is the one on their
 * wall, and the two disagree for hours every day: east of UTC the server is
 * behind, west of it the server is ahead.
 *
 * The countdown moved to the browser, and the server's window gained
 * {@link CALENDAR_WINDOW_SLACK_DAYS} at each end so the browser always has the
 * rows it needs to make the real cut. Without that slack, a reader in Los
 * Angeles at 18:00 on Diwali gets a response windowed from *tomorrow* and the
 * single most important date on the card is simply absent on the day it happens.
 *
 * ## NO COUNTRY, NO CALENDAR
 *
 * `tenants.country` is nullable and mostly unset. A workspace without one gets
 * `country: null` and an empty list, and the card renders nothing.
 *
 * The alternative was defaulting to the one dataset that exists, and it is
 * exactly what this product was told not to do: Wend is sold to retailers,
 * hotels, resorts, banks and event companies, and **nothing about it may assume
 * a country any more than it may assume a line of business**. A Diwali card on a
 * Munich hotel's dashboard is worse than no card.
 */
import { z } from 'zod';

import { ownMomentSchema } from './own-moment.js';

/* -------------------------------------------------------------------------- */
/* The window                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * How far ahead the calendar looks.
 *
 * Sixty days, because that is the lead time the owner asked for — far enough out
 * that a campaign can still be planned, briefed and built, rather than a
 * reminder that arrives the week of.
 */
export const CALENDAR_WINDOW_DAYS = 60;

/**
 * Extra days served at each end of the window so the browser can make the real
 * cut against the reader's own date.
 *
 * One day is enough for every offset on earth: UTC−12 to UTC+14 puts the
 * server's date at most one day either side of the reader's. The browser then
 * filters to exactly `[0, CALENDAR_WINDOW_DAYS]`, so the slack never reaches a
 * screen — it exists only so the row is *available* to be filtered.
 */
export const CALENDAR_WINDOW_SLACK_DAYS = 1;

/**
 * The largest gap between two dates of one festival that still counts as one
 * run of days rather than two occurrences.
 *
 * One: strictly consecutive. See the header — three days was the first value and
 * it silently moved Raksha Bandhan 2026 two days earlier.
 */
export const CALENDAR_RUN_GAP_DAYS = 1;

/* -------------------------------------------------------------------------- */
/* The countries a dataset exists for                                          */
/* -------------------------------------------------------------------------- */

/**
 * Two-letter codes Wend holds a calendar for. India is the first, not the shape
 * of the feature — adding a country is a dataset plus an entry in
 * {@link CALENDAR_MOMENTS}, and no code below knows which one it is looking at.
 */
export const CALENDAR_COUNTRY_VALUES = ['IN'] as const;
export const calendarCountrySchema = z.enum(CALENDAR_COUNTRY_VALUES);
export type CalendarCountry = z.infer<typeof calendarCountrySchema>;

export function isCalendarCountry(value: string | null): value is CalendarCountry {
  return value !== null && (CALENDAR_COUNTRY_VALUES as readonly string[]).includes(value);
}

/* -------------------------------------------------------------------------- */
/* The curation, and the words                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Source slug to the name people use, per country.
 *
 * Membership **is** the curation: a slug that is not a key here never reaches a
 * screen. So this table answers both questions at once — is this worth showing,
 * and what is it called — and the two can never drift apart.
 *
 * Every key is checked against the extracted dataset by `verify-calendar`, in
 * **every year it covers**: a slug present in one year and absent from the next
 * is a festival that silently stops appearing, which is what a per-dataset check
 * alone would miss.
 */
export const CALENDAR_MOMENTS: Readonly<
  Record<CalendarCountry, Readonly<Record<string, string>>>
> = {
  IN: {
    /* The autumn run, which is the commercial peak of the Indian year. */
    /*
     * Dhanteras is the largest retail selling day in India — gold, appliances,
     * vehicles — and it is **not in the extracted tables at all**. Review found
     * it missing and the owner supplied the dates; they live in
     * `data/supplied-in.json` rather than in the extractor's own output, which
     * is overwritten whenever a year is re-extracted.
     */
    dhanteras: 'Dhanteras',
    'naraka-chaturdasi': 'Choti Diwali',
    'festival-of-lights': 'Diwali',
    'day-after-diwali': 'Govardhan Puja',
    'bhai-duj': 'Bhai Dooj',
    'chhath-puja': 'Chhath Puja',
    vijayadashami: 'Dussehra',
    'durga-puja': 'Durga Puja',
    'ganesh-chaturthi': 'Ganesh Chaturthi',
    'raksha-bandhan': 'Raksha Bandhan',
    janmashtami: 'Janmashtami',
    onam: 'Onam',

    /* Spring and the harvests. */
    'festival-of-colors': 'Holi',
    'day-before-the-festival-of-colors': 'Holika Dahan',
    'maha-shivratri': 'Maha Shivratri',
    ugadi: 'Ugadi',
    vaisakhi: 'Baisakhi',
    pongal: 'Pongal',
    'makar-sankranti': 'Makar Sankranti',
    'tamil-new-years-day': 'Tamil New Year',
    'bengali-new-years-day': 'Bengali New Year',
    'parsi-new-years-day': 'Parsi New Year',

    /* The two Eids, under the names anybody would search for. */
    'festival-of-breaking-the-fast': 'Eid al-Fitr',
    'feast-of-the-sacrifice': 'Eid al-Adha',

    /* Birthdays that are national moments rather than observances. */
    'guru-nanaks-birthday': 'Guru Nanak Jayanti',
    'mahaviras-birthday': 'Mahavir Jayanti',
    'buddhas-birthday': 'Buddha Purnima',
    'doctor-ambedkars-birthday': 'Ambedkar Jayanti',

    /* The three gazetted national days. */
    'republic-day': 'Republic Day',
    'independence-day': 'Independence Day',
    'mahatma-gandhis-birthday': 'Gandhi Jayanti',

    /* Christian dates, which are public holidays here and retail dates anywhere. */
    'good-friday': 'Good Friday',
    easter: 'Easter',
    'christmas-day': 'Christmas',

    /* The secular commercial dates, which are not holidays at all and are
       nonetheless what half of retail plans around. */
    'new-years-day': "New Year's Day",
    'valentines-day': "Valentine's Day",
    'mothers-day': "Mother's Day",
    'fathers-day': "Father's Day",
    'friendship-day': 'Friendship Day',
  },
};

/* -------------------------------------------------------------------------- */
/* The shape                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * One **day**, and everything worth campaigning on that falls on it.
 *
 * `names` rather than `name` because a planning day is the unit — see the
 * header. And deliberately **no countdown**: the reader's "today" is the one on
 * their wall, which only the browser knows, so `daysAway` is computed there.
 */
export const calendarEntrySchema = z
  .object({
    /** ISO date, in the calendar's own country — no time, because a date is not an instant. */
    date: z.string().min(1),
    /** At least one. What people call them, never the source's wording. */
    names: z.array(z.string().min(1)).min(1),
  })
  .strict();
export type CalendarEntry = z.infer<typeof calendarEntrySchema>;

export const calendarResponseSchema = z
  .object({
    generatedAt: z.string().min(1),
    /**
     * `null` when the workspace has no country set, or has one Wend holds no
     * dataset for. Both render nothing, and they are the same to a reader: this
     * product does not know which country's dates apply to you.
     */
    country: calendarCountrySchema.nullable(),
    windowDays: z.literal(CALENDAR_WINDOW_DAYS),
    /**
     * The last date the country's dataset holds, or `null` with no country.
     *
     * **Without this, running out of data is indistinguishable from a quiet
     * stretch.** The shipped Indian dataset ends 2027-12-31, so from 2 November
     * 2027 the window reaches past it and from 26 December 2027 it is empty
     * forever — and the card would have drawn the same nothing it draws for a
     * genuinely uneventful two months. "Nothing is coming up" and "Wend's
     * calendar stops here" are different statements and only one of them is
     * true.
     */
    /**
     * This workspace's own moments — **every one it has**, past and future,
     * never windowed, never grouped, never collapsed, never merged into
     * `entries`.
     *
     * A sibling array rather than a field on `calendarEntrySchema`, and that is
     * deliberate three times over. The entry schema is `.strict()` with exactly
     * `date` and `names`, and the strictness is load-bearing: it is what keeps a
     * countdown off the wire, which is what fixed the UTC+05:30 wrong-day bug,
     * and three suites pin the spelling. `upcomingMoments` drops any slug that
     * is not a key in `CALENDAR_MOMENTS`, so a moment routed through it would be
     * deleted in silence. And its date grouping dedupes on the display name, so
     * a moment somebody named "Diwali" would vanish into the festival.
     *
     * Unwindowed because a row a person cannot see is a row they cannot remove.
     * The card cuts to the reader's own window for the list and the grid, and
     * lists everything under "Your moments". {@link MAX_OWN_MOMENTS} bounds it.
     *
     * Required, not optional: `[]` and "the server did not say" are different
     * facts, and only one of them is a fact. Every fixture in every suite gains
     * `ownMoments: []`, which zod and the compiler find for you.
     */
    ownMoments: z.array(ownMomentSchema),
    coversUntil: z.string().min(1).nullable(),
    /** Soonest first, one per date. Empty when nothing falls inside the window. */
    entries: z.array(calendarEntrySchema),
  })
  .strict();
export type CalendarResponse = z.infer<typeof calendarResponseSchema>;

/* -------------------------------------------------------------------------- */
/* The fold                                                                    */
/* -------------------------------------------------------------------------- */

/** Whole days between two ISO dates, by UTC midnight, so no zone shifts it. */
export function daysBetween(fromIso: string, toIso: string): number {
  const from = Date.parse(`${fromIso}T00:00:00.000Z`);
  const to = Date.parse(`${toIso}T00:00:00.000Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return Number.NaN;
  return Math.round((to - from) / 86_400_000);
}

/**
 * The curated, run-collapsed, date-grouped entries around the window.
 *
 * Pure, and `today` is passed in rather than read, so the fold has no clock in
 * it and a suite can stand on any date it likes.
 *
 * It returns **slack included** — `[-1, +61]` rather than `[0, 60]` — because
 * the caller that knows the reader's real date is the browser. See the header.
 */
export function upcomingMoments(
  country: CalendarCountry,
  rows: readonly { readonly date: string; readonly slug: string }[],
  today: string,
): CalendarEntry[] {
  const named = CALENDAR_MOMENTS[country];

  const inWindow = rows
    .filter((row) => Object.prototype.hasOwnProperty.call(named, row.slug))
    .map((row) => ({ ...row, away: daysBetween(today, row.date) }))
    .filter((row) => Number.isFinite(row.away))
    .filter(
      (row) =>
        row.away >= -CALENDAR_WINDOW_SLACK_DAYS &&
        row.away <= CALENDAR_WINDOW_DAYS + CALENDAR_WINDOW_SLACK_DAYS,
    )
    .sort((a, b) => (a.date === b.date ? a.slug.localeCompare(b.slug) : a.date.localeCompare(b.date)));

  /*
   * Collapse a run of CONSECUTIVE dates for one slug to its first day.
   *
   * The anchor is the last date **seen** for that slug, not the last one kept,
   * so a five-day run collapses to one entry rather than to its first and
   * fourth. Keeping only the last-kept date was the original rule and its
   * comment claimed the behaviour this one actually has.
   */
  const lastSeen = new Map<string, string>();
  const keptBySlug: { readonly date: string; readonly slug: string }[] = [];
  for (const row of inWindow) {
    const previous = lastSeen.get(row.slug);
    lastSeen.set(row.slug, row.date);
    if (previous !== undefined && daysBetween(previous, row.date) <= CALENDAR_RUN_GAP_DAYS) {
      continue;
    }
    keptBySlug.push(row);
  }

  /*
   * Then group by date. A planning day is the unit: four festivals on 14 April
   * are one day a marketer plans one campaign for, not four rows with identical
   * chips and identical countdowns.
   */
  const byDate = new Map<string, string[]>();
  for (const row of keptBySlug) {
    const names = byDate.get(row.date) ?? [];
    const name = named[row.slug] ?? row.slug;
    if (!names.includes(name)) names.push(name);
    byDate.set(row.date, names);
  }

  return [...byDate.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, names]) => ({ date, names }));
}

/**
 * Whether `date` may still be added, judged against `today` with `slackDays` of
 * tolerance.
 *
 * Two callers with two different `today`s, and the difference is the whole date
 * story here. The browser passes `localToday(new Date())` and **zero** slack,
 * because it knows the reader's date exactly. The server passes the UTC date and
 * {@link CALENDAR_WINDOW_SLACK_DAYS}, because it does not.
 */
export function ownMomentDateIsAddable(
  date: string,
  today: string,
  slackDays: number,
): boolean {
  const away = daysBetween(today, date);
  return Number.isFinite(away) && away >= -slackDays;
}
